// Thin fetch wrapper over /api/*, shared by every module's API client.
// SWA forwards the signed-in user to the Function App automatically, so no
// Authorization header is set here — credentials ride on the SWA session cookie.

export class ApiError extends Error {
  status: number;
  fields?: Record<string, string>; // per-field messages from a 400 validation response

  constructor(status: number, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields;
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });

  if (!res.ok) {
    // Our Functions return { error, fields? }. SWA's own rejections (401/403 from
    // a route rule) return HTML instead, so parse defensively either way.
    const body = (await res.json().catch(() => null)) as
      | { error?: string; fields?: Record<string, string> }
      | null;
    throw new ApiError(res.status, body?.error ?? res.statusText, body?.fields);
  }

  if (res.status === 204) return undefined as T;

  // A 200 that isn't JSON means something intercepted the call — typically SWA
  // serving the SPA shell for an unmatched /api route. Fail loudly rather than
  // letting a JSON parse error surface as a mystery.
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiError(res.status, `Expected JSON from ${path}, got "${contentType || "no content-type"}".`);
  }

  return (await res.json()) as T;
}
